import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import AcademyNav from '../../components/academy/AcademyNav';
import CategoryCard from '../../components/academy/CategoryCard';
import ArticleCard from '../../components/academy/ArticleCard';
import CtaBand from '../../components/academy/CtaBand';
import Seo from '../../components/academy/Seo';
import BrandHero from '../../components/BrandHero';
import { getCategories, getArticles } from '../../lib/academyApi';

const HERO_IMG =
  'https://images.pexels.com/photos/20131971/pexels-photo-20131971.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920';

const AcademyHome = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    getArticles({ featured: true, limit: 9 }).then((d) => setFeatured(d.articles)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo
        title="Know More. Buy Smarter."
        description="Florida's trusted resource for buying, inspecting, and owning used vehicles. Premium buying guides, inspection education, vehicle history and more from CarLens."
        path="/academy"
        image={HERO_IMG}
        breadcrumbs={[{ name: 'Academy', path: '/academy' }]}
      />
      <BrandHero />
      <AcademyNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={HERO_IMG} alt="Premium used vehicle" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e0e10] via-[#0e0e10]/85 to-[#0e0e10]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e10] via-transparent to-transparent" />
        <div className="absolute -top-20 left-1/3 w-[600px] h-[300px] bg-orange-500/20 blur-[140px] rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-orange-300">
              <BookOpen className="w-4 h-4" /> CarLens Academy
            </div>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.02] tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              Know More.<br />
              <span className="text-orange-500">Buy Smarter.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-zinc-300 leading-relaxed max-w-xl">
              Florida's trusted resource for buying, inspecting, and owning used vehicles.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/academy/category/buying-guides')}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-7 py-4 rounded-full shadow-lg shadow-orange-500/25 transition-all hover:scale-[1.02]"
                data-testid="hero-browse-guides"
              >
                Browse Buying Guides <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/book')}
                className="inline-flex items-center justify-center gap-2 border border-zinc-600 hover:border-white/60 text-white font-semibold px-7 py-4 rounded-full transition-colors backdrop-blur-sm"
                data-testid="hero-book-inspection"
              >
                Book an Inspection
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 pb-20 space-y-20 -mt-6">
        {/* Categories */}
        <section>
          <div className="mb-8">
            <p className="text-orange-500 text-xs font-bold tracking-[0.25em] uppercase mb-2">Explore</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Featured Categories
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((c) => (
              <CategoryCard key={c.slug} category={c} />
            ))}
          </div>
        </section>

        {/* Featured Articles */}
        <section>
          <div className="flex items-end justify-between mb-8 gap-4">
            <div>
              <p className="text-orange-500 text-xs font-bold tracking-[0.25em] uppercase mb-2">Start Here</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                Featured Guides
              </h2>
            </div>
            <button
              onClick={() => navigate('/academy/articles')}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300"
              data-testid="view-all-articles"
            >
              View all articles <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </section>

        <CtaBand />
      </div>
    </div>
  );
};

export default AcademyHome;
